import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';
import { useLocation } from 'react-router-dom';

import { Button } from 'ui-components/components/button';
import { Modal } from 'ui-components/components/modal';
import { ModalHeader } from 'ui-components/components/modal/modal-header';
import { ModalBody } from 'ui-components/components/modal/modal-body';
import { ModalFooter } from 'ui-components/components/modal/modal-footer';
import { LoadingIndicator } from 'ui-components/components/loading-indicator';
import { OperatingSystemService } from 'ui-components/services/operating-system-service';
import { TooltipPosition } from 'ui-components/components/tooltip/tooltip-position';

import { deletePreset } from '../../../store/seller/presets/seller.presets.actions';
import { emptyArray } from 'ui-components/default-props/empty';
import { printHotkeysAndBarcode } from '../../../store/printing/print.actions';
import Global from '../../../store/global-selectors';
import { ManageItemGrid } from '../../manage-item-grid';
import ModalPortal from '../../modal-portal';
import { usePrevious } from '../../../hooks/use-previous';
import { useModal } from '../../../hooks/use-modal';
import { EditPresetModalContainer } from '../../../containers/edit-preset-modal-container';
import { useBarcodeScanningFeatureGate } from '../../../feature-gates/use-barcode-scanning-feature-gate';
import style from './manage-presets-modal.module.less';

const messages = defineMessages({
  name: {
    description: 'managePresets.name',
    defaultMessage: 'Name',
    id: 'HurEYV',
  },
  hotkey: {
    description: 'managePresets.hotkey',
    defaultMessage: 'Hotkey',
    id: 'E92NNW',
  },
  title: {
    description: 'managePresetsModal.title',
    defaultMessage: 'Manage Presets',
    id: 'lwUWTX',
  },
  newPreset: {
    description: 'managePresetsModal.newPreset',
    defaultMessage: 'New Preset',
    id: 'mTqoIM',
  },
  printHotkeysAndBarcode: {
    description: 'managePresetsModal.printHotkeysAndBarcode',
    defaultMessage: 'Print Hotkeys and Barcodes',
    id: 'TtR/Ed',
  },
  done: {
    description: 'managePresetsModal.close',
    defaultMessage: 'Done',
    id: 'G/xCcL',
  },
});

export const ManagePresetsModal = React.memo(({ onClose }) => {
  const dispatch = useDispatch();
  const presets = useSelector(Global.selectSellerPresets) || emptyArray;
  const isLoading = useSelector(Global.selectSellerManagePresetsLoadingState);
  const { open, close, isOpen } = useModal();
  const [presetToEdit, setPresetToEdit] = useState();

  const { isFeatureEnabled, disabledMessage } = useBarcodeScanningFeatureGate();

  const openEditModal = useCallback(
    (preset) => {
      setPresetToEdit(preset?.presetId ? preset : { presetOptions: {} });
      open();
    },
    [open],
  );

  const { formatMessage } = useIntl();
  const presetHotkeys = presets.reduce((acc, preset) => {
    if (preset.hotKey) {
      acc.push(preset);
    }

    return acc;
  }, []);
  const { pathname } = useLocation();
  const prevPathname = usePrevious(pathname);

  const managePresetsItemGridColumns = [
    {
      name: formatMessage(messages.name),
      itemRenderer: (preset) => preset.name,
    },
    {
      name: formatMessage(messages.hotkey),
      itemRenderer: (preset) => {
        const presetHotkey = presetHotkeys.find((record) => record.hotKey === preset.hotKey);

        if (presetHotkey) {
          const ctrlOrCmd = OperatingSystemService.isMac() ? 'Cmd' : 'Ctrl';

          return `${ctrlOrCmd} + ${presetHotkey.hotKey.toUpperCase()}`;
        }

        return '';
      },
      width: 99,
    },
  ];

  const onItemDeleted = useCallback(
    (preset) => {
      dispatch(deletePreset(preset.presetId));
    },
    [dispatch],
  );

  const onPrintHotkeysAndBarcode = useCallback(() => {
    dispatch(printHotkeysAndBarcode());
  }, [dispatch]);

  // Close ManagePresetsModal when user clicks on the Ship From drop-down error message
  useEffect(() => {
    if (prevPathname && prevPathname !== pathname) {
      onClose();
    }
  }, [onClose, pathname, prevPathname]);

  return (
    <ModalPortal onClose={onClose}>
      <Modal className={style['manage-presets-modal']}>
        <ModalHeader>
          <FormattedMessage {...messages.title} />
        </ModalHeader>
        <ModalBody className={style['modal-body']}>
          {isLoading && (
            <div className={style['loading-indicator-container']}>
              <LoadingIndicator className={style['loading-indicator']} />
            </div>
          )}
          {!isLoading && (
            <ManageItemGrid
              columns={managePresetsItemGridColumns}
              items={presets}
              onItemDeleted={onItemDeleted}
              onItemEdited={openEditModal}
            />
          )}
        </ModalBody>
        <ModalFooter className={style['manage-presets-modal-footer']}>
          <div className={style['first-link']}>
            <Button className={style['button-link']} onClick={openEditModal} theme='link'>
              <FormattedMessage {...messages.newPreset} />
            </Button>
          </div>
          <div className={style['manage-presets-link-and-button']}>
            <Button
              className={style['button-link']}
              disabled={!isFeatureEnabled}
              disabledTooltipMessage={disabledMessage && <FormattedMessage {...disabledMessage} />}
              disabledTooltipMessagePosition={TooltipPosition.Bottom}
              onClick={onPrintHotkeysAndBarcode}
              theme='link'
            >
              <FormattedMessage {...messages.printHotkeysAndBarcode} />
            </Button>
            <Button onClick={onClose} theme='primary'>
              <FormattedMessage {...messages.done} />
            </Button>
          </div>
        </ModalFooter>
        {isOpen && (
          <EditPresetModalContainer
            isEdit={!!presetToEdit?.presetId}
            onClose={close}
            preset={presetToEdit}
            updatePreset={setPresetToEdit}
          />
        )}
      </Modal>
    </ModalPortal>
  );
});

ManagePresetsModal.propTypes = {
  onClose: PropTypes.func.isRequired,
};
